import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import DRP from '../../../components/Core/DRP/DRP'
import numeral from 'numeral'

import { useIiko, getRanges } from '../../../api/useIiko'
import getUniqueArrayByKey from '../../../helpers/getUniqueArrayByKey'
import { getPlaceTargets, getPlaceEmployeeTargets, getCompanyEmployeesAll } from '../../../api'

import styles from './styles.module.scss'

import SummaryCards from './SummaryCards'
import SalesChart from './SalesChart'
import DishCategoryProfitability from './DishCategoryProfitability'
import AutomatedTips from './AutomatedTips'
import EmployeeCost from './EmployeeCost'
import PosControl from './PosControl'
import SalesByEmployee from './SalesByEmployee'
import EmployeeProfitability from './EmployeeProfitability'
import RevenueByCategory from './RevenueByCategory'
import TopBorrowedMaterials from './TopBorrowedMaterials'
import RevenueBySupplier from './RevenueBySupplier'
import RevenueByWarehouse from './RevenueByWarehouse'
import ProductsConsumption from './ProductsConsumption'
import RevenueByCategoryTips from './RevenueByCategoryTips'
import { SettingsIcon } from './icons'
import SettingsModal from './SettingsModal'

const calculateSaleTarget = (target, start, end) => {
  let dailyAmount = target.amount
  if (target.target_type === 'monthly') {
    const daysInMonth = moment(start).daysInMonth()
    dailyAmount = target.amount / daysInMonth
  }
  const daysInRange = moment(end).diff(moment(start), 'days') + 1
  return dailyAmount * daysInRange
}

const getCountEmployeesForDate = (employees, date) => {
  return employees.reduce((acc, employee) => {
    return [...acc, ...(employee.work_times[date] ? [employee] : [])]
  }, [])
}

const calculateEmployeeCost = (employee, date) => {
  const workTimes = employee.work_times[date] || []
  return workTimes.reduce((acc, time) => {
    return acc + time.total_work_cost
  }, 0)
}

const geCountEmployeesForDay = (employees, ranges, hour) => {
  const start = moment(ranges.from).startOf('day')
  const end = moment(ranges.to).endOf('day')
  return employees.reduce((acc, employee) => {
    const workTimes = Object.entries(employee.work_times).filter(([date]) => {
      const workDate = moment(date)
      return workDate.isBetween(start, end, null, '[]')
    })
    const isWorking = workTimes.some(([date, times]) => {
      return times.some(time => {
        const dateWithHour = moment(date).hour(hour)
        if (dateWithHour.isBetween(moment(time.started_at), moment(time.finished_at), null, '[]')) {
          return true
        }
        return false
      })
    })
    return [...acc, ...(isWorking ? [employee] : [])]
  }, [])
}

const ShiftProfitability = ({companyId, placeId, selectedPlace}) => {
  const { t } = useTranslation()

  const makeRequest = useIiko(companyId)

  const contentRef = useRef()
  const settingsModalRef = useRef()

  const [filterDate, setFilterDate] = useState({
    start: moment().subtract(1, 'month').startOf('month'),
    end: moment().subtract(1, 'month').endOf('month'),
    // start: moment('2026-06-25'),
    // end: moment('2026-06-25'),
    // start: moment().subtract(1, 'week').startOf('isoWeek'),
    // end: moment().subtract(1, 'week').endOf('isoWeek'),
    // start: moment().startOf('isoWeek'),
    // end: moment().add(1, 'day').endOf('isoWeek'),
  })
  
  const [{sales, loading: salesLoading}, setSales] = useState({loading: false, sales: []})
  const [{prevSales, loading: prevSalesLoading}, setPrevSales] = useState({loading: false, prevSales: []})
  const [{employees, loading: employeesLoading}, setEmployees] = useState({loading: false, employees: []})
  const [{transactions, loading: transactionsLoading}, setTransactions] = useState({loading: false, transactions: []})
  const [{prosuctsInStore, loading: productsInStoreLoading}, setProductsInStore] = useState({loading: false, prosuctsInStore: []})
  const [zReportSummary, setZReportSummary] = useState({
    loading: false,
    current: {},
    previous: {},
  })
  const [targets, setTargets] = useState({
    sales: 0,
    employeeCostPercentage: 0,
    materialCostPercentage: 0,
  })
  const [employeesTargets, setEmployeesTargets] = useState([])
  const [companyEmployees, setCompanyEmployees] = useState([])

  const ranges = getRanges(filterDate.start, filterDate.end)

  const summary = useMemo(() => {
    const { totalSales, totalSalesWithoutVat, rawMaterialCost } = sales.reduce((acc, item) => {
      return {
        ...acc,
        totalSales: acc.totalSales + (item.DishDiscountSumInt || 0),
        totalSalesWithoutVat: acc.totalSalesWithoutVat + (item['DishDiscountSumInt.withoutVAT'] || 0),
        rawMaterialCost: acc.rawMaterialCost + (item['ProductCostBase.ProductCost'] || 0),
      }
    }, {
      totalSales: 0,
      totalSalesWithoutVat: 0,
      rawMaterialCost: 0,
    })
    const { employeeCost, employeeDuration } = employees.reduce((acc, employee) => {
      return {
        employeeCost: acc.employeeCost + ((employee.profitability?.cost || 0) * Number(employee.em_status || 1)) * (employee.work_duration / 60),
        employeeDuration: acc.employeeDuration + (employee.work_duration || 0),
      }
    }, {
      employeeCost: 0,
      employeeDuration: 0,
    })

    const billsCount = getUniqueArrayByKey(sales, 'OrderNum').length
    return {
      totalSales: totalSales,
      totalSalesWithoutVat: totalSalesWithoutVat,
      billsCount: billsCount,
      avarageCheck: billsCount > 0 ? totalSales / billsCount : 0,
      rawMaterialCost: rawMaterialCost,
      rawMaterialCostPct: totalSales > 0 ? rawMaterialCost / totalSales * 100 : 0,
      employeeCost: employeeCost,
      employeeCostPct: totalSales > 0 ? employeeCost / totalSales * 100 : 0,
      employeeDuration: employeeDuration,
      grossProfit: totalSales - rawMaterialCost - employeeCost,
      grossProfitPct: totalSales > 0 ? (totalSales - rawMaterialCost - employeeCost) / totalSales * 100 : 0,
    }
  }, [sales, employees])

  const prevSummary = useMemo(() => {
    const { totalSales, totalSalesWithoutVat, rawMaterialCost } = prevSales.reduce((acc, item) => {
      return {
        ...acc,
        totalSales: acc.totalSales + (item.DishDiscountSumInt || 0),
        totalSalesWithoutVat: acc.totalSalesWithoutVat + (item['DishDiscountSumInt.withoutVAT'] || 0),
        rawMaterialCost: acc.rawMaterialCost + (item['ProductCostBase.ProductCost'] || 0),
      }
    }, {
      totalSales: 0,
      totalSalesWithoutVat: 0,
      rawMaterialCost: 0,
    })
    const { employeeCost, employeeDuration } = employees.reduce((acc, employee) => {
      return {
        employeeCost: acc.employeeCost + ((employee.profitability?.cost || 0) * Number(employee.em_status || 1)) * (employee.work_duration / 60),
        employeeDuration: acc.employeeDuration + (employee.work_duration || 0),
      }
    }, {
      employeeCost: 0,
      employeeDuration: 0,
    })

    const billsCount = getUniqueArrayByKey(prevSales, 'OrderNum').length
    return {
      totalSales: totalSales,
      totalSalesWithoutVat: totalSalesWithoutVat,
      billsCount: billsCount,
      avarageCheck: billsCount > 0 ? totalSales / billsCount : 0,
      rawMaterialCost: rawMaterialCost,
      rawMaterialCostPct: totalSales > 0 ? rawMaterialCost / totalSales * 100 : 0,
      employeeCost: employeeCost,
      employeeCostPct: totalSales > 0 ? employeeCost / totalSales * 100 : 0,
      employeeDuration: employeeDuration,
      grossProfit: totalSales - rawMaterialCost - employeeCost,
      grossProfitPct: totalSales > 0 ? (totalSales - rawMaterialCost - employeeCost) / totalSales * 100 : 0,
    }
  }, [prevSales, employees])

  const chartData = useMemo(() => {
    const salesByDate = sales.reduce((acc, sale) => {
      return {
        ...acc,
        [sale['OpenDate.Typed']]: acc[sale['OpenDate.Typed']] ? [...acc[sale['OpenDate.Typed']], sale] : [sale],
      }
    }, {})
    const salesByHour = sales.reduce((acc, sale) => {
      return {
        ...acc,
        [sale.HourClose]: acc[sale.HourClose] ? [...acc[sale.HourClose], sale] : [sale],
      }
    }, {})
    return Object.entries(ranges.diff > 1 ? salesByDate : salesByHour).map(([date, sales]) => {
      const employeesMeta = ranges.diff > 1 ? getCountEmployeesForDate(employees, date) : geCountEmployeesForDay(employees, ranges.current, date)
      const originalDate = ranges.diff > 1 ? moment(date).format('YYYY-MM-DD') : moment(ranges.current.from).hour(date).format('YYYY-MM-DD')
      return {
        date: ranges.diff > 7 ? moment(date).format('MM.DD') : moment(date).format('dddd, MMM Do'),
        hour: date,
        originalDate: originalDate,
        sales: sales.reduce((acc, sale) => acc + (sale.DishDiscountSumInt || 0), 0),
        productCost: sales.reduce((acc, sale) => acc + (sale['ProductCostBase.ProductCost'] || 0), 0),
        bills: getUniqueArrayByKey(sales, 'OrderNum').length,
        employees:  ranges.diff > 1 ? getCountEmployeesForDate(employees, date).length : geCountEmployeesForDay(employees, ranges.current, date).length,
        employeesMeta: employeesMeta,
        employeesCost: employeesMeta.reduce((acc, emp) =>  acc + calculateEmployeeCost(emp, originalDate), 0),
      }
    })
  }, [sales, ranges, employees])

  useEffect(() => {
    const rect = contentRef.current.getBoundingClientRect()
    const distance = window.innerHeight - rect.top;
    contentRef.current.style.height = `${distance - 35}px`
  }, [])

  useEffect(() => {
    getCompanyEmployeesAll(companyId).then(res => {
      if (Array.isArray(res?.users)) {
        setCompanyEmployees(res.users)
      }
    })
  }, [companyId])

  useEffect(() => {
    getSales()
    getEmployeeWorks()
    getTransactions()
    getZReportSummary()
    getStores()
    getTargets()
  }, [selectedPlace?.external_id, filterDate.start.format('YYYY-MM-DD'), filterDate.end.format('YYYY-MM-DD')])

  const getTargets = async () => {
    const [targets, employeesTargets] = await Promise.all([
      getPlaceTargets(companyId, placeId || 'all'),
      getPlaceEmployeeTargets(companyId, placeId || 'all')
    ])
    if (Array.isArray(employeesTargets)) {
      setEmployeesTargets(employeesTargets)
    }
    if (targets) {
      if (Array.isArray(targets)) {
        const aggregated = targets.reduce((acc, target) => {
          return {
            ...acc,
            sales: acc.sales + calculateSaleTarget(target, filterDate.start, filterDate.end),
            totalEmployeePercent: targets.reduce((sum, t) => sum + t.employee_cost, 0),
            totalMaterialPercent: targets.reduce((sum, t) => sum + t.material_cost, 0),
          }
        }, {
          sales: 0,
        })
        setTargets({
          sales: aggregated.sales,
          employeeCostPercentage: aggregated.totalEmployeePercent / targets.length,
          materialCostPercentage: aggregated.totalMaterialPercent / targets.length,
        })
        return
      }
      setTargets({
        sales: calculateSaleTarget(targets, filterDate.start, filterDate.end),
        employeeCostPercentage: targets.employee_cost,
        materialCostPercentage: targets.material_cost,
      })
    }
  }

  const getSales = async () => {
    setSales(prev => ({...prev, loading: true}))
    const ranges = getRanges(filterDate.start, filterDate.end)
    const [salesRes, prevSalesRes] = await Promise.all([
      makeRequest('sales', {department_id: selectedPlace?.external_id, ...ranges.current}).send(),
      makeRequest('sales', {department_id: selectedPlace?.external_id, ...ranges.previous}).send(),
    ])
    setSales({loading: false, sales: Array.isArray(salesRes?.data) ? salesRes.data : []})
    // setPrevSales({loading: false, prevSales: Array.isArray(prevSalesRes?.data) ? prevSalesRes.data : []})
  }

  const getEmployeeWorks = async () => {
    const res = await makeRequest('employees', {department_id: selectedPlace?.external_id, ...ranges.current}).send()
    if (Array.isArray(res)) {
      setEmployees({loading: false, employees: res})
    }
  }

  const getTransactions = async () => {
    setTransactions(prev => ({...prev, loading: true}))
    const ranges = getRanges(filterDate.start, filterDate.end)
    const res = await makeRequest('transactions', {department_id: selectedPlace?.external_id, ...ranges.current}).send()
    setTransactions({loading: false, transactions: Array.isArray(res?.data) ? res.data : []})
  }

  const getZReportSummary = async () => {
    setZReportSummary(prev => ({...prev, loading: true}))
    const ranges = getRanges(filterDate.start, filterDate.end)
    const [currentRes, previousRes] = await Promise.all([
      makeRequest('z-report-summary', {department_id: selectedPlace?.external_id, ...ranges.current}).send(),
      // makeRequest('z-report-summary', {department_id: selectedPlace?.external_id, ...ranges.previous}).send()
    ])
    setZReportSummary({loading: false, current: currentRes ?? {}})
  }

  const getStores = async () => {
    setProductsInStore(prev => ({...prev, loading: true}))
    const res = await makeRequest('warehouse', {department_id: selectedPlace?.external_id, ...ranges.current}).send()
    setProductsInStore({loading: false, prosuctsInStore: Array.isArray(res) ? res : []})
  }

  const handleChangeDate = ({startDate, endDate}) => {
    setFilterDate({start: moment(startDate), end: moment(endDate)})
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div style={{flex: 1}}>
          <div className={styles.headerTitle}>{placeId ? `${selectedPlace.name} ${t('shift profitability')}` : t('Shift Profitability')}</div>
          <div className={styles.headerDesc}>{t('POS sales · raw material cost · labor costs on one screen')}</div>
        </div>
        <DRP
          right={true}
          additionalRanges={[
            {
              label: 'This year',
              startDate: moment().startOf('year').toDate(),
              endDate: moment().toDate()
            }, {
              label: 'Last year',
              startDate: moment().subtract(1, 'year').startOf('year').toDate(),
              endDate: moment().subtract(1, 'year').endOf('year').toDate()
            }
          ]}
          definedRangesStyle={{right: 40}}
          initRange={{startDate: filterDate.start.toDate(), endDate: filterDate.end.toDate()}}
          onChange={handleChangeDate} />
        {
          placeId
            ? <div className={styles.settingsButton} onClick={settingsModalRef.current?.open}>
                <SettingsIcon />
              </div>
            : null
        }
      </div>
      <div className={styles.content} ref={contentRef}>
        <SummaryCards
          loading={salesLoading || prevSalesLoading || zReportSummary.loading}
          data={{
            current: {
              salesAfterDiscount: numeral(numeral(summary.totalSales).format('0.[000]')).value(),
              salesTarget: numeral(numeral(targets.sales).format('0.[000]')).value(),
              netSalesWithoutVat: summary.totalSalesWithoutVat,
              bills: summary.billsCount,
              averageCheck: summary.avarageCheck,
              cost: summary.rawMaterialCost,
              foodCostPct: summary.rawMaterialCostPct,
              employeeCost: summary.employeeCost,
              employeeCostPct: summary.employeeCostPct,
              employeeDuration: summary.employeeDuration,
              grossProfit: summary.grossProfit,
              grossMarging: summary.grossProfitPct,
              zShiftTotal: numeral(numeral(zReportSummary.current.zShiftTotal).format('0.[000]')).value(),
              statuses: zReportSummary.current.statuses,
              employeeCostPercentage: targets.employeeCostPercentage,
              materialCostPercentage: targets.materialCostPercentage,
            },
            previous: {
              salesAfterDiscount: prevSummary.totalSales,
              netSalesWithoutVat: prevSummary.totalSalesWithoutVat,
              bills: prevSummary.billsCount,
              averageCheck: prevSummary.avarageCheck,
              cost: prevSummary.rawMaterialCost,
              foodCostPct: prevSummary.rawMaterialCostPct,
              employeeCost: prevSummary.employeeCost,
              employeeCostPct: prevSummary.employeeCostPct,
              employeeDuration: prevSummary.employeeDuration,
              grossProfit: prevSummary.grossProfit,
              grossMarging: prevSummary.grossProfitPct,
            },
          }} />
        <SalesChart salesChart={chartData} type={ranges.diff > 1 ? 'mounthly' : 'hourly'} />
        <div className={styles.grid_70_30}>
          <DishCategoryProfitability
            loading={salesLoading}
            sales={sales} />
          <AutomatedTips
            loading={salesLoading || employeesLoading}
            sales={sales}
            chartData={chartData}
            rawMaterialCostPct={summary.rawMaterialCostPct}
            employeeCostPct={summary.employeeCostPct} />
        </div>
        <div className={styles.grid_70_30}>
          <EmployeeCost
            loading={employeesLoading}
            employees={employees}
            sales={sales}
            targetEmployeeCostPercentage={targets.employeeCostPercentage}
            totalSales={summary.totalSales} />
          <PosControl sales={sales} />
        </div>
        <SalesByEmployee
          loading={salesLoading || employeesLoading}
          sales={sales}
          companyEmployees={companyEmployees}
          employees={employees}
          employeesTargets={employeesTargets} />
        <EmployeeProfitability
          loading={salesLoading || employeesLoading}
          sales={sales}
          employees={employees} />
        <div className={styles.grid_70_30}>
          <RevenueByCategory
            loading={salesLoading || transactionsLoading}
            sales={sales}
            transactions={transactions} />
          <RevenueByCategoryTips
            from={filterDate.start}
            to={filterDate.end}
            sales={sales}
            transactions={transactions} />
        </div>
        <TopBorrowedMaterials
          loading={transactionsLoading}
          transactions={transactions} />
        <div className={styles.grid_70_30}>
          <RevenueBySupplier
            loading={transactionsLoading}
            transactions={transactions} />
          <RevenueByWarehouse
            loading={transactionsLoading}
            transactions={transactions} />
        </div>
        <ProductsConsumption
          loading={salesLoading || productsInStoreLoading}
          productsInStore={prosuctsInStore}
          transactions={transactions} />
      </div>
      <SettingsModal
        ref={settingsModalRef}
        companyId={companyId}
        placeId={placeId} />
    </div>
  )
}

export default ShiftProfitability
